import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const STOP_COLOR = '#2fae7a';
const BASE_COLOR = '#f4b740';
const SELECTED_COLOR = '#16b8c4';

function wishlistIcon(active = false) {
  const bg = active ? SELECTED_COLOR : '#f4b740';
  return L.divIcon({
    className: 'map-pin map-pin--wishlist',
    html: `<span class="map-pin__badge map-pin__badge--wish" style="background:${bg}">★</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function numberedIcon(num, active = false) {
  const bg = active ? SELECTED_COLOR : STOP_COLOR;
  return L.divIcon({
    className: 'map-pin map-pin--stop',
    html: `<span class="map-pin__badge" style="background:${bg}">${num}</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function baseIcon(active = false) {
  const bg = active ? SELECTED_COLOR : BASE_COLOR;
  return L.divIcon({
    className: 'map-pin map-pin--base',
    html: `<span class="map-pin__badge map-pin__badge--base" style="background:${bg}">⌂</span>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function markerIcon(m) {
  if (m.kind === 'base') return baseIcon(m.active);
  if (m.kind === 'wishlist') return wishlistIcon(m.active);
  return numberedIcon(m.index ?? 1, m.active);
}

export function createMapPanel(containerEl) {
  let map = null;
  let markerLayer = null;
  let routeLayer = null;
  let markerById = new Map();
  let routePolyline = null;
  let fitTimer = null;
  let onSelectHandler = null;

  function ensureMap() {
    if (map) return map;
    map = L.map(containerEl, {
      zoomControl: true,
      attributionControl: true,
    }).setView([46.15, 14.5], 8);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>',
    }).addTo(map);

    markerLayer = L.layerGroup().addTo(map);
    routeLayer = L.layerGroup().addTo(map);
    return map;
  }

  function destroy() {
    if (fitTimer) clearTimeout(fitTimer);
    markerById.clear();
    routePolyline = null;
    if (map) {
      map.remove();
      map = null;
      markerLayer = null;
      routeLayer = null;
    }
  }

  function invalidateSize() {
    map?.invalidateSize({ animate: false });
  }

  function clearMarkers() {
    markerById.clear();
    markerLayer?.clearLayers();
  }

  function clearRoute() {
    routePolyline = null;
    routeLayer?.clearLayers();
  }

  /**
   * Incremental marker sync — avoids full layer rebuild on each geocode tick.
   */
  function syncMarkers(markers, onSelect) {
    ensureMap();
    onSelectHandler = onSelect ?? onSelectHandler;
    const nextIds = new Set(markers.map((m) => m.id));

    for (const [id, marker] of markerById) {
      if (!nextIds.has(id)) {
        markerLayer.removeLayer(marker);
        markerById.delete(id);
      }
    }

    for (const m of markers) {
      const icon = markerIcon(m);
      const existing = markerById.get(m.id);
      if (existing) {
        existing.setLatLng([m.lat, m.lng]);
        existing.setIcon(icon);
        existing.off('click');
        existing.on('click', () => onSelectHandler?.(m.id));
      } else {
        const marker = L.marker([m.lat, m.lng], { icon, title: m.label });
        marker.on('click', () => onSelectHandler?.(m.id));
        marker.addTo(markerLayer);
        markerById.set(m.id, marker);
      }
    }
  }

  /** Full rebuild — use when switching trips or resetting state. */
  function setMarkers(markers, onSelect) {
    clearMarkers();
    syncMarkers(markers, onSelect);
  }

  function setRoute(polylineCoords) {
    ensureMap();
    if (routePolyline && polylineCoords?.length >= 2) {
      const latlngs = polylineCoords.map((c) => [c.lat, c.lng]);
      routePolyline.setLatLngs(latlngs);
      return;
    }

    clearRoute();
    if (!polylineCoords?.length || polylineCoords.length < 2) return;

    const latlngs = polylineCoords.map((c) => [c.lat, c.lng]);
    routePolyline = L.polyline(latlngs, {
      color: '#4aa3df',
      weight: 4,
      opacity: 0.85,
      lineJoin: 'round',
    }).addTo(routeLayer);
  }

  function fitToMarkers(padding = [32, 32]) {
    if (!map || markerById.size === 0) return;
    const group = L.featureGroup([...markerById.values()]);
    map.fitBounds(group.getBounds().pad(0.12), { padding });
  }

  function fitToMarkersDebounced(delayMs = 300, padding = [32, 32]) {
    if (fitTimer) clearTimeout(fitTimer);
    fitTimer = setTimeout(() => {
      fitTimer = null;
      fitToMarkers(padding);
    }, delayMs);
  }

  function flyTo(lat, lng, zoom = 13) {
    ensureMap();
    map.flyTo([lat, lng], zoom, { duration: 0.6 });
  }

  function highlightMarker(id) {
    const marker = markerById.get(id);
    if (marker && map) {
      map.panTo(marker.getLatLng(), { animate: true });
    }
  }

  return {
    ensureMap,
    destroy,
    invalidateSize,
    setMarkers,
    syncMarkers,
    setRoute,
    fitToMarkers,
    fitToMarkersDebounced,
    flyTo,
    highlightMarker,
  };
}
